import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowContainer } from 'react-tiny-popover';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEqual, cloneDeep, isEmpty } from 'lodash-es';

import positionPopover from '../../../_shared/utils/positionPopover';
import {
  FilterArrow,
  HeaderType,
  FilterType
} from '../../../_shared/constants/filters';
import PopoverWrapper from '../../../_shared/components/Popover';
import Loading from '../../../_shared/components/Loading';
import CheckBoxFilter from '../../../_shared/components/CheckBoxFilter';
import FilterService from '../../../services/FilterService';

import {
  StyledMakeModelFilter,
  Label,
  FilterIndicator,
  RightArrow
} from './Styles';

const propTypes = {
  filterMetaData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.bool])
  ).isRequired,
  updateSelectedModels: PropTypes.func.isRequired
};

const ModelFilter = ({ filterMetaData, updateSelectedModels }) => {
  const intl = useIntl();

  const { models } = filterMetaData;

  const [currentModels, setCurrentModels] = useState(models);

  useEffect(() => {
    setCurrentModels(cloneDeep(models));
  }, [models]);

  const updateModels = useCallback(
    list => {
      if (isEqual(list, models)) {
        return;
      }

      updateSelectedModels(filterMetaData, list, FilterType.Model);
    },
    [filterMetaData, models, updateSelectedModels]
  );

  const popoverChildFn = useCallback(
    () => openPopOver => (
      <StyledMakeModelFilter onClick={() => openPopOver(isOpen => !isOpen)}>
        <Label>
          <FormattedMessage id="list.filters.models" />
        </Label>
        {!isEmpty(currentModels) && (
          <FilterIndicator data-testid="model-filter">
            <span data-testid="model-filter-selected">
              {FilterService.formatItemsSelected(
                currentModels,
                {
                  all: 'list.filters.allSelected',
                  some: 'list.filters.numberSelected'
                },
                true
              )}
            </span>
            <RightArrow />
          </FilterIndicator>
        )}

        {isEmpty(currentModels) && <Loading height="auto" width="102px" />}
      </StyledMakeModelFilter>
    ),
    [currentModels]
  );

  const popOverContenFn = useCallback(
    () => (openPopOver, { childRect, popoverRect }) => (
      <ArrowContainer
        position={FilterArrow.position}
        childRect={childRect}
        popoverRect={popoverRect}
        arrowColor={FilterArrow.color}
        arrowSize={FilterArrow.size}
      >
        <CheckBoxFilter
          filterList={currentModels}
          headerType={HeaderType.Controlled}
          searchPlaceHolder={intl.formatMessage({
            id: 'list.filters.searchModels'
          })}
          allSelectedText={intl.formatMessage({ id: 'list.filters.allModels' })}
          openPopOver={openPopOver}
          searchEnabled
          hasChild
          handleFilterApply={updateModels}
          shouldNotTranslate
          testId="model-filter"
        />
      </ArrowContainer>
    ),
    [currentModels, intl, updateModels]
  );

  return (
    <PopoverWrapper
      align="end"
      position={['right']}
      contentLocation={positionPopover()}
      renderChild={popoverChildFn()}
      renderContent={popOverContenFn()}
    />
  );
};

ModelFilter.propTypes = propTypes;

export default ModelFilter;
